import { appendDescription } from "../localization/catalog.js";
import { bindEvent } from "./events.js";
export function keyed(document, tag, id) {
    const element = document.createElement(tag);
    element.dataset.e308Key = id;
    return element;
}
export function applyStyle(element, style) {
    if (!style)
        return;
    if (style.className)
        element.classList.add(...style.className.split(/\s+/).filter(Boolean));
    if (style.color)
        element.style.color = style.color;
    if (style.background)
        element.style.background = style.background;
    if (style.borderColor)
        element.style.borderColor = style.borderColor;
    if (style.width)
        element.style.width = style.width;
    if (style.maxWidth)
        element.style.maxWidth = style.maxWidth;
    if (style.height)
        element.style.height = style.height;
}
export function appendMark(parent, mark, resolver) {
    if (!mark)
        return;
    const badge = parent.ownerDocument.createElement("span");
    badge.className = "e308-mark";
    badge.dataset.tone = mark.tone ?? "neutral";
    badge.textContent = resolver.text(mark.label);
    parent.append(badge);
}
export function renderQuantity(document, line, resolver) {
    const row = document.createElement("span");
    row.className = "e308-quantity";
    const value = resolver.argument({
        kind: "quantity",
        value: line.value,
        ...(line.format ? { format: line.format } : {}),
    });
    row.textContent = `${resolver.text(line.label)}: ${value}`;
    row.dataset.resource = line.resourceId;
    return row;
}
export function renderResource(document, resource, resolver) {
    const element = document.createElement("div");
    element.className = "e308-resource";
    element.append(renderQuantity(document, resource, resolver));
    if (resource.rate !== undefined) {
        const rate = resolver.argument({ kind: "quantity", value: resource.rate });
        element.append(document.createTextNode(` (${rate}/s)`));
    }
    if (resource.capacity !== undefined) {
        const capacity = resolver.argument({ kind: "quantity", value: resource.capacity });
        element.title = `capacity ${capacity}`;
    }
    return element;
}
export function renderAction(document, action, resolver, dispatch, startHold, domIdPrefix = "e308-action") {
    const button = document.createElement("button");
    button.type = "button";
    if (action.domId)
        button.id = action.domId;
    const state = action.state ?? (action.enabled ? "available" : "unavailable");
    const interactive = action.enabled && state !== "pending";
    button.textContent = resolver.text(action.label);
    button.dataset.action = action.id;
    button.dataset.state = state;
    button.dataset.tone = action.tone ?? "primary";
    button.setAttribute("aria-disabled", String(!interactive));
    button.setAttribute("aria-label", resolver.text(action.label));
    if (state === "pending")
        button.setAttribute("aria-busy", "true");
    if (action.tooltip)
        button.title = resolver.text(action.tooltip);
    if (action.description) {
        const description = document.createElement("span");
        description.className = "e308-action-description";
        appendDescription(description, action.description, resolver);
        if (!action.tooltip)
            button.title = description.textContent ?? "";
        button.append(description);
    }
    if (action.blockers.length > 0)
        button.dataset.blockers = action.blockers.map((item) => item.kind).join(" ");
    appendActionDetails(button, action, resolver, domIdPrefix);
    bindEvent(button, "click", () => {
        if (interactive &&
            (!action.confirm || document.defaultView?.confirm(resolver.text(action.confirm)) !== false)) {
            dispatch(action.intent);
        }
    });
    const hold = action.hold;
    if (hold && startHold) {
        bindEvent(button, "pointerdown", (event) => {
            if (interactive && event.button === 0)
                startHold(hold, event);
        });
    }
    return button;
}
function appendActionDetails(button, action, resolver, domIdPrefix) {
    for (const [kind, lines] of [
        ["costs", action.costs],
        ["rewards", action.rewards],
    ]) {
        if (!lines || lines.length === 0)
            continue;
        const details = button.ownerDocument.createElement("span");
        details.className = `e308-action-${kind}`;
        for (const line of lines)
            details.append(renderQuantity(button.ownerDocument, line, resolver));
        button.append(details);
    }
    if (action.blockers.length > 0) {
        const blockers = button.ownerDocument.createElement("span");
        blockers.className = "e308-action-blockers";
        blockers.id = `${domIdPrefix}-blockers`;
        blockers.setAttribute("role", "list");
        blockers.setAttribute("aria-label", "Unavailable because");
        for (const blocker of action.blockers) {
            const reason = button.ownerDocument.createElement("span");
            reason.className = "e308-action-blocker";
            reason.setAttribute("role", "listitem");
            reason.textContent = blockerText(blocker, resolver);
            blockers.append(reason);
        }
        button.setAttribute("aria-describedby", blockers.id);
        button.append(blockers);
    }
}
function blockerText(blocker, resolver) {
    const quantity = (value) => resolver.argument({ kind: "quantity", value });
    switch (blocker.kind) {
        case "insufficient":
            return `${blocker.resourceId}: need ${quantity(blocker.required)}, have ${quantity(blocker.available)}`;
        case "capacity-blocked":
            return `${blocker.resourceId}: capacity ${quantity(blocker.capacity)}`;
        case "locked":
            return `requires ${blocker.prerequisiteIds.join(", ")}`;
        case "cooldown":
            return `${blocker.actionId}: ${Math.ceil(blocker.remainingMs / 1_000)} seconds remaining`;
        case "invalid-count":
            return "enter a valid purchase count";
        case "allocation-exceeded":
            return `${blocker.allocationId}: ${quantity(blocker.assigned)} of ${quantity(blocker.budget)} assigned`;
        case "stale-revision":
            return "the game state changed; try again";
        case "invalid-target":
            return `unknown target: ${blocker.id}`;
        case "disabled":
            return blocker.reasonKey.replaceAll("-", " ");
        case "budget-exceeded":
            return `${blocker.budgetId}: limit reached`;
        case "numeric-fault":
        case "transaction-failed":
            return blocker.message;
        case "extension":
            return blocker.fallbackKey.replaceAll("-", " ");
    }
}
//# sourceMappingURL=elements.js.map